import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { waLink } from "../lib/constants";
import { WhatsAppIcon } from "./icons";

const LINKS = [
  { id: "collections", label: "Les Collections", testid: "nav-link-collections" },
  { id: "maison", label: "La Maison", testid: "nav-link-maison" },
  { id: "experience", label: "L'Expérience", testid: "nav-link-experience" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { settings } = useShop();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waHref = settings.whatsapp_number
    ? waLink(settings.whatsapp_number, "Bonjour, je souhaite découvrir la collection Vāsam.")
    : "/parfums";

  const goTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.__lenis?.scrollTo(`#${id}`, { offset: -70 }), 400);
    } else {
      window.__lenis?.scrollTo(`#${id}`, { offset: -70 });
    }
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-[100] transition-all duration-500 ${
          scrolled ? "border-b border-espresso/10 bg-ivory/85 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="wrap flex h-20 items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-3">
            <span className="font-serif text-3xl leading-none text-espresso">Vāsam</span>
            <span className="hidden text-[8px] uppercase tracking-[0.4em] text-champagne sm:block">Maison de fragrances</span>
          </Link>

          <nav className="hidden items-center gap-10 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => goTo(l.id)}
                className="text-[11px] uppercase tracking-[0.28em] text-espresso/75 transition-colors duration-300 hover:text-champagne"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/parfums"
              data-testid="nav-link-parfums"
              className="text-[11px] uppercase tracking-[0.28em] text-espresso/75 transition-colors duration-300 hover:text-champagne"
            >
              La Collection
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            <a
              data-testid="nav-cta-whatsapp"
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="btn-pill hidden bg-espresso px-7 py-3.5 text-ivory hover:bg-noir sm:inline-flex"
            >
              <WhatsAppIcon /> Commander
            </a>
            <button
              data-testid="nav-toggle"
              onClick={() => setOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-espresso/15 text-espresso lg:hidden"
              aria-label="Ouvrir le menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-lenis-prevent
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[150] flex flex-col bg-espresso px-6 py-6"
          >
            <div className="flex items-center justify-between">
              <span className="font-serif text-3xl text-ivory">Vāsam</span>
              <button
                data-testid="nav-close"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-ivory/25 text-ivory"
                aria-label="Fermer le menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-7">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.6 }}
                  onClick={() => goTo(l.id)}
                  className="text-left font-serif text-5xl text-ivory transition-colors hover:text-champagne"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42, duration: 0.6 }}>
                <Link to="/parfums" onClick={() => setOpen(false)} className="font-serif text-5xl text-ivory transition-colors hover:text-champagne">
                  La Collection
                </Link>
              </motion.div>
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="mt-auto"
            >
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid="nav-mobile-whatsapp"
                className="btn-pill w-full border border-champagne/50 py-4 text-champagne"
              >
                <WhatsAppIcon /> Commander sur WhatsApp
              </a>
              <p className="mt-6 text-center text-[10px] tracking-[0.2em] text-ivory/40">
                Consultante Chogan indépendante — distribution indépendante
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
